import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { apiFetch } from "../api/api";
import { logClientError } from "../api/errorLogger";
import TenantForm from "../components/TenantForm";
import TenantTable from "../components/TenantTable";

export default function Tenants() {
  const location = useLocation();
  const [tenants, setTenants] = useState([]);
  const [editingTenant, setEditingTenant] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const [documents, setDocuments] = useState({});
  const [selectedFiles, setSelectedFiles] = useState({});

  const fetchDocuments = useCallback(async (tenantId) => {
    const docs = await apiFetch(`/documents?tenantId=${tenantId}`);
    setDocuments((prev) => ({ ...prev, [tenantId]: docs }));
  }, []);

  const fetchTenants = useCallback(async () => {
    const data = await apiFetch("/tenants");
    setTenants(data);

    await Promise.all(data.map((t) => fetchDocuments(t.id)));
  }, [fetchDocuments]);


  const handleSubmit = async (form) => {
    setError("");

    try {
      if (editingTenant) {
        await apiFetch(`/tenants/${editingTenant.id}`, {
          method: "PATCH",
          body: JSON.stringify(form),
        });
      } else {
        await apiFetch("/tenants", {
          method: "POST",
          body: JSON.stringify(form),
        });
      }

      setEditingTenant(null);
      setShowForm(false);
      await fetchTenants();
    } catch (err) {
      setError(err.message);
      logClientError(err, { page: "Tenants", action: "createOrUpdateTenant" });
    }
  };

  const deleteTenant = async (id) => {
    setError("");

    try {
      await apiFetch(`/tenants/${id}`, { method: "DELETE" });
      await fetchTenants();
    } catch (err) {
      setError(err.message);
      logClientError(err, { page: "Tenants", action: "deleteTenant", tenantId: id });
    }
  };


  const uploadDocument = async (tenantId) => {
    const file = selectedFiles[tenantId];
    if (!file) return;
    setError("");

    const formData = new FormData();
    formData.append("file", file);

    try {
      await apiFetch(`/documents/tenant/${tenantId}`, {
        method: "POST",
        body: formData,
      });

      await fetchDocuments(tenantId);
    } catch (err) {
      setError(err.message);
      logClientError(err, { page: "Tenants", action: "uploadDocument", tenantId });
    }
  };

  const deleteDocument = async (docId, tenantId) => {
    setError("");

    try {
      await apiFetch(`/documents/${docId}`, { method: "DELETE" });
      await fetchDocuments(tenantId);
    } catch (err) {
      setError(err.message);
      logClientError(err, { page: "Tenants", action: "deleteDocument", tenantId, docId });
    }
  };

  const loadTenants = useCallback(async () => {
    setError("");
    setIsLoading(true);

    try {
      await fetchTenants();
    } catch (err) {
      setError(err.message);
      logClientError(err, { page: "Tenants", action: "loadTenants" });
    } finally {
      setIsLoading(false);
    }
  }, [fetchTenants]);

  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  useEffect(() => {
    if (location.state?.action === "add") {
      setEditingTenant(null);
      setShowForm(true);
    }
  }, [location.state]);

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <p className="admin-page-eyebrow">Mailbox Manager</p>
          <h2>Tenants</h2>
        </div>

        <button
          className="admin-button admin-button-primary"
          onClick={() => {
            setEditingTenant(null);
            setShowForm(true);
          }}
        >
          Add Tenant
        </button>
      </div>

      {error && <p className="admin-empty-state admin-error-state">{error}</p>}

      {isLoading ? (
        <p className="admin-empty-state">Loading tenants...</p>
      ) : (
        <TenantTable
          tenants={tenants}
          documents={documents}
          selectedFiles={selectedFiles}
          onEdit={(t) => {
            setEditingTenant(t);
            setShowForm(true);
          }}
          onDelete={deleteTenant}
          onFileChange={(id, file) =>
            setSelectedFiles((prev) => ({ ...prev, [id]: file }))
          }
          onUpload={uploadDocument}
          onDeleteDoc={deleteDocument}
        />
      )}

      {showForm && (
        <div className="admin-form-panel">
          <TenantForm
            tenant={editingTenant}
            onSubmit={handleSubmit}
            onCancel={() => {
              setShowForm(false);
              setEditingTenant(null);
            }}
          />
        </div>
      )}
    </div>
  );
}
